import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Container, Card, Alert, Spinner, Button, Form } from 'react-bootstrap';
import api, { formatApiErrorDetail } from '../services/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Token de verificación no proporcionado');
        return;
      }

      try {
        const response = await api.get(`/api/v1/email/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verificado exitosamente');
      } catch (error) {
        setStatus('error');
        const errorMessage = formatApiErrorDetail(error, 'Error al verificar el email. El token puede ser inválido o haber expirado.');
        setMessage(errorMessage);
        
        // Si el error menciona que el token ya fue usado o expiró, mostrar opción de reenvío
        if (errorMessage.includes('utilizado') || errorMessage.includes('expirado')) {
          setShowResend(true);
        }
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      setResendMessage('Por favor ingresa tu correo electrónico');
      return;
    }

    setResendLoading(true);
    setResendMessage('');
    try {
      const response = await api.post('/api/v1/email/resend-verification', { email: resendEmail });
      setResendMessage(response.data.message || 'Se ha enviado un nuevo enlace de verificación');
      setShowResend(false);
    } catch (error) {
      setResendMessage(formatApiErrorDetail(error, 'Error al reenviar el email de verificación'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex align-items-center"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <Container>
        <div className="d-flex justify-content-center">
          <Card className="shadow-lg border-0" style={{ maxWidth: '500px', width: '100%' }}>
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <img 
                  src="/LOGO-DIRESA.png" 
                  alt="Logo DIRESA" 
                  className="mb-3"
                  style={{ maxHeight: '80px', width: 'auto' }}
                />
                <h2 className="fw-bold text-primary mb-2">Verificación de Email</h2>
              </div>

              {status === 'loading' && (
                <div className="text-center">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <p>Verificando tu correo electrónico...</p>
                </div>
              )}

              {status === 'success' && (
                <Alert variant="success" className="mb-4">
                  <Alert.Heading>¡Verificación Exitosa!</Alert.Heading>
                  <p>{message}</p>
                </Alert>
              )}

              {status === 'error' && (
                <>
                  <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error de Verificación</Alert.Heading>
                    <p>{message}</p>
                  </Alert>
                  
                  {showResend && (
                    <Card className="mb-4">
                      <Card.Body>
                        <h5 className="mb-3">¿Necesitas un nuevo enlace de verificación?</h5>
                        {resendMessage && (
                          <Alert variant={resendMessage.includes('enviado') ? 'success' : 'danger'} className="mb-3">
                            {resendMessage}
                          </Alert>
                        )}
                        <Form onSubmit={handleResendVerification}>
                          <Form.Group className="mb-3">
                            <Form.Label>Correo Electrónico</Form.Label>
                            <Form.Control
                              type="email"
                              placeholder="tu@email.com"
                              value={resendEmail}
                              onChange={(e) => setResendEmail(e.target.value)}
                              required
                              disabled={resendLoading}
                            />
                          </Form.Group>
                          <Button 
                            variant="primary" 
                            type="submit"
                            disabled={resendLoading}
                            className="w-100"
                          >
                            {resendLoading ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Enviando...
                              </>
                            ) : (
                              'Reenviar Enlace de Verificación'
                            )}
                          </Button>
                        </Form>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}

              <div className="text-center">
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/login')}
                  className="me-2"
                >
                  Ir a Iniciar Sesión
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => navigate('/')}
                >
                  Volver al Inicio
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default VerifyEmail;

